import { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    ArrowLeft, ArrowUpRight, CalendarCheck, Download, Clock, Sparkles, Quote as QuoteIcon,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, CONTACT } from "@/data/translations";
import { ARTICLES, getArticleBySlug } from "@/data/articles";
import { LangToggle } from "@/components/LangToggle";
import { Footer } from "@/components/Footer";

const SEO_JSONLD_ID = "fw-article-jsonld";

/**
 * Render inline markdown-lite: **bold** → <strong>, _em_ → <em>.
 * Bold is critical for AI/SEO discoverability of technical terms
 * (Locus Coeruleus, BrainPAD, VO2 Max, Forward Head Posture, ...).
 */
const renderInline = (text) => {
    if (!text) return null;
    // Split keeping the delimiters so we can map them. Bold has priority.
    const parts = text.split(/(\*\*[^*]+\*\*|_[^_]+_)/g);
    return parts.map((part, i) => {
        // Stable key: part value + index. Inline markdown rendering is read-only
        // (no reorder), but combining the content with the index avoids React
        // identity churn when adjacent parts happen to be identical.
        const key = `${part}-${i}`;
        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <strong key={key} className="text-white font-semibold">
                    {part.slice(2, -2)}
                </strong>
            );
        }
        if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
            return (
                <em key={key} className="italic text-white/90">
                    {part.slice(1, -1)}
                </em>
            );
        }
        return <span key={key}>{part}</span>;
    });
};

const ArticleHeader = ({ t }) => (
    <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-5 flex items-center justify-between">
            <Link to="/" data-testid="article-back-link" className="flex items-center gap-3 group">
                <picture>
                    <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                    <img
                        src={BRAND.logoDark}
                        alt="Fit & Wellness"
                        className="h-9 md:h-10 w-auto"
                        decoding="async"
                        width="120"
                        height="40"
                    />
                </picture>
            </Link>
            <div className="flex items-center gap-4">
                <LangToggle testid="article-language-toggle" />
                <Link
                    to="/#insights"
                    className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-fw transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    {t.article.backToInsights}
                </Link>
            </div>
        </div>
    </header>
);

const formatDate = (iso, lang) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch (_e) {
        return iso;
    }
};

const ArticleMeta = ({ article, lang, t }) => (
    <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-[0.24em] text-white/55 mb-8">
        <span className="inline-flex items-center gap-1.5 text-fw">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            {article.category[lang]}
        </span>
        <span className="w-1 h-1 rounded-full bg-white/30" />
        <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" strokeWidth={2} />
            {article.readTime} {t.insights.readTimeLabel}
        </span>
        <span className="w-1 h-1 rounded-full bg-white/30" />
        <span>{formatDate(article.publishedAt, lang)}</span>
    </div>
);

const BlockRenderer = ({ block, i }) => {
    if (block.type === "h2") {
        return (
            <h2
                key={i}
                className="font-heavy text-2xl md:text-3xl uppercase text-white mt-12 mb-4 leading-[1.1]"
            >
                {renderInline(block.text)}
            </h2>
        );
    }
    if (block.type === "quote") {
        return (
            <blockquote
                key={i}
                className="my-10 pl-6 border-l-2 border-fw bg-fw/[0.06] py-5 pr-5 rounded-sm"
            >
                <QuoteIcon className="w-5 h-5 text-fw mb-3" strokeWidth={1.5} />
                <p className="font-display text-xl md:text-2xl text-white leading-snug italic">
                    {renderInline(block.text)}
                </p>
            </blockquote>
        );
    }
    return (
        <p key={i} className="text-base md:text-lg text-white/80 leading-relaxed mb-5">
            {renderInline(block.text)}
        </p>
    );
};

const ArticleFooterCTA = ({ article, t }) => {
    const guideHref = `/resources/${article.relatedGuideFile}`;
    return (
        <div
            data-testid="article-footer-cta"
            className="mt-16 md:mt-20 border border-fw/40 bg-fw/[0.05] rounded-sm p-7 md:p-10"
        >
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fw block mb-3">
                {t.article.ctaOverline}
            </span>
            <h3 className="font-heavy text-2xl md:text-3xl uppercase text-white leading-tight mb-3">
                {t.article.ctaHeadline}
            </h3>
            <p className="text-sm md:text-base text-white/70 leading-relaxed max-w-2xl mb-7">
                {t.article.ctaBody}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
                <a
                    href={guideHref}
                    download
                    data-testid="article-cta-download-guide"
                    className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-fw text-black font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green transition-all duration-300"
                >
                    <span className="flex items-center gap-2">
                        <Download className="w-4 h-4" strokeWidth={2} />
                        {t.article.ctaDownloadGuide}
                    </span>
                    <ArrowUpRight
                        className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={2}
                    />
                </a>
                <a
                    href={CONTACT.calendlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="article-cta-calendly"
                    className="group inline-flex items-center justify-between gap-3 px-6 py-4 border border-white/30 text-white font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                >
                    <span className="flex items-center gap-2">
                        <CalendarCheck className="w-4 h-4" strokeWidth={2} />
                        {t.article.ctaConsult}
                    </span>
                    <ArrowUpRight
                        className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        strokeWidth={2}
                    />
                </a>
            </div>
        </div>
    );
};

const RelatedArticles = ({ currentSlug, lang, t }) => {
    const others = ARTICLES.filter((a) => a.slug !== currentSlug).slice(0, 2);
    if (!others.length) return null;
    return (
        <div className="mt-20 pt-12 border-t border-white/10">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw block mb-6">
                {t.article.relatedTitle}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {others.map((other) => (
                    <Link
                        key={other.slug}
                        to={`/insights/${other.slug}`}
                        data-testid={`article-related-${other.slug}`}
                        className="group flex items-start gap-4 p-5 border border-white/10 rounded-sm hover:border-fw/50 hover:bg-fw/[0.04] transition-colors"
                    >
                        <span className="flex-none w-12 h-12 rounded-sm bg-fw/15 border border-fw/30 flex items-center justify-center font-heavy text-lg text-fw">
                            {String(ARTICLES.findIndex((a) => a.slug === other.slug) + 1).padStart(2, "0")}
                        </span>
                        <div className="flex-1">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/45 block mb-1">
                                {other.category[lang]}
                            </span>
                            <p className="font-display text-base font-semibold text-white group-hover:text-fw transition-colors leading-snug">
                                {other.title[lang]}
                            </p>
                        </div>
                        <ArrowUpRight
                            className="w-4 h-4 text-white/40 group-hover:text-fw transition-colors"
                            strokeWidth={2}
                        />
                    </Link>
                ))}
            </div>
        </div>
    );
};

export const Article = () => {
    const { slug } = useParams();
    const { t, lang } = useLanguage();
    const article = useMemo(() => getArticleBySlug(slug), [slug]);

    // SEO: update document head dynamically for AI/Google discoverability.
    // SPA-friendly: title + meta description + og tags + Article JSON-LD schema.
    useEffect(() => {
        if (!article) return;
        const title = article.title[lang];
        const description = article.excerpt[lang];
        const keywords = article.seoKeywords ? article.seoKeywords[lang] : "";
        const fullTitle = `${title} | Fit & Wellness — Luca La Capria`;
        const url = `${window.location.origin}/insights/${article.slug}`;
        const absoluteImage = article.image.startsWith("http")
            ? article.image
            : `${window.location.origin}${article.image}`;

        const prev = {
            title: document.title,
            html: document.documentElement.lang,
        };
        document.title = fullTitle;
        document.documentElement.lang = lang;

        const setMeta = (selector, attr, content) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                const [key, val] = selector.replace(/[[\]"]/g, "").split("=");
                el.setAttribute(key, val);
                document.head.appendChild(el);
            }
            el.setAttribute(attr, content);
        };

        setMeta('meta[name="description"]', "content", description);
        if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
        setMeta('meta[property="og:type"]', "content", "article");
        setMeta('meta[property="og:title"]', "content", fullTitle);
        setMeta('meta[property="og:description"]', "content", description);
        setMeta('meta[property="og:image"]', "content", absoluteImage);
        setMeta('meta[property="og:url"]', "content", url);
        setMeta('meta[property="twitter:title"]', "content", fullTitle);
        setMeta('meta[property="twitter:description"]', "content", description);
        setMeta('meta[property="twitter:image"]', "content", absoluteImage);

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.rel = "canonical";
            document.head.appendChild(canonical);
        }
        canonical.href = url;

        // JSON-LD Article schema — what AIs and Google use to understand the page
        const existing = document.getElementById(SEO_JSONLD_ID);
        if (existing) existing.remove();
        const jsonLd = document.createElement("script");
        jsonLd.id = SEO_JSONLD_ID;
        jsonLd.type = "application/ld+json";
        jsonLd.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description: description,
            keywords: keywords,
            image: [absoluteImage],
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            inLanguage: lang === "it" ? "it-IT" : "en-US",
            articleSection: article.category[lang],
            wordCount: article.content[lang].blocks
                .map((b) => b.text.split(/\s+/).length)
                .reduce((a, b) => a + b, 0),
            author: {
                "@type": "Person",
                name: "Luca La Capria",
                jobTitle: "Personal Trainer, Preparatore Atletico, Consulente Alimentare",
                url: window.location.origin,
                sameAs: [
                    "https://wa.me/393519520592",
                    "https://calendly.com/fit-wellness2021/30min",
                ],
            },
            publisher: {
                "@type": "Organization",
                name: "Fit & Wellness",
                logo: {
                    "@type": "ImageObject",
                    url: `${window.location.origin}/assets/logo-dark.png`,
                },
            },
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": url,
            },
        });
        document.head.appendChild(jsonLd);

        return () => {
            document.title = prev.title;
            document.documentElement.lang = prev.html;
            const remove = document.getElementById(SEO_JSONLD_ID);
            if (remove) remove.remove();
        };
    }, [article, lang]);

    if (!article) {
        return <Navigate to="/404" replace />;
    }

    const content = article.content[lang];

    return (
        <div className="min-h-screen bg-black text-white flex flex-col">
            <ArticleHeader t={t} />

            <main data-testid="article-section" className="relative flex-1 overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
                <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-fw/10 blur-[150px] pointer-events-none" />

                <article className="relative z-10 mx-auto max-w-[820px] w-full px-5 md:px-10 py-16 md:py-24">
                    <Link
                        to="/#insights"
                        data-testid="article-breadcrumb"
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-white/55 hover:text-fw transition-colors mb-10"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                        {t.article.backToInsights}
                    </Link>

                    <ArticleMeta article={article} lang={lang} t={t} />

                    <h1
                        data-testid="article-headline"
                        className="font-heavy text-4xl md:text-5xl lg:text-6xl uppercase leading-[1.0] text-balance mb-8"
                    >
                        {article.title[lang]}
                    </h1>

                    <div className="relative aspect-[16/9] overflow-hidden bg-fw/[0.06] mb-10 rounded-sm">
                        <picture>
                            {article.imageWebp && (
                                <source srcSet={article.imageWebp} type="image/webp" />
                            )}
                            <img
                                src={article.image}
                                alt={article.imageAlt[lang]}
                                loading="eager"
                                decoding="async"
                                width="1600"
                                height="900"
                                className="w-full h-full object-cover"
                            />
                        </picture>
                    </div>

                    <p
                        data-testid="article-lede"
                        className="font-display text-xl md:text-2xl text-fw leading-snug mb-10"
                    >
                        {renderInline(content.lede)}
                    </p>

                    <div data-testid="article-content">
                        {content.blocks.map((block, i) => (
                            <BlockRenderer key={`${block.type}-${i}`} block={block} i={i} />
                        ))}
                    </div>

                    <div className="mt-12 pt-6 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                        {t.article.signature}
                    </div>

                    <ArticleFooterCTA article={article} t={t} />
                    <RelatedArticles currentSlug={article.slug} lang={lang} t={t} />
                </article>
            </main>

            <Footer />
        </div>
    );
};

export default Article;
